import { TranslationContext } from 'contexts/TranslationContext'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { useSelector } from 'react-redux'
import { TIME_CODE_CONTAINER_LOCATION, TIME_CODE_STATE } from 'config/constants/timecode'
import { ViewSettings } from 'config/constants/editorSettings'
import { selectViewSettings } from 'selectors/mainView'
import MediaProgress from '../../AssetPanels/Media/MediaProgress'
import { DEFAULT_FPS, RangeTools } from '~/Util'
import * as Actions from '~/actions'
import { ReactComponent as IconRendering } from '~/assets/video-player/ic_rendering.svg'
import { ReactComponent as IconUpload } from '~/assets/video-player/ic_upload.svg'
import { FILE_STATUS, PLAYBACK_STATE } from '~/enums'
import { useAction, useBind } from '~/hooks'
import { useDnDPreview } from '~/hooks/preview/dnd/useDnDPreview'
import { getObjectTimeCode } from '~/Util/timecode'
import BasePreviewPlayer from '../BasePreviewPlayer'
import Preview from '../Preview'
import { usePlayer } from '../lib'
import usePlayerState from '../lib/usePlayerState'
import useMediaPreviewState from '../useMediaPreviewState'
import { getFrameStep } from '~/helpers/getFrameStep'
import { SourceImagePreview } from './sourcePreview/SourceImagePreview'
import { SourceTextPreview } from './sourcePreview/SourceTextPreview'
import './MediaPreview.scss'

// ---

function useFrameStepHandlers(fps, {
  seek,
  usePlayerCallback,
  previewPlayerType,
}) {
  const pause = useAction(Actions.playback.changeClipPlaybackState, PLAYBACK_STATE.PAUSE)
  const { timeCodeState } = useSelector(state => state.playback)
  const activePreview = useSelector(state => state.preview.activePreview)
  const timeMode = useSelector(state => state.playback.timeCodeMode)

  const onFrameStep = usePlayerCallback(React.useCallback(
    (player, forward) => {
      if (timeCodeState !== TIME_CODE_STATE.EDIT
        && (previewPlayerType === activePreview)) {
        const progress = player.getCurrentTime()
        const duration = player.getDuration()
        const { frameCount, dropFrame } = getObjectTimeCode(
          progress, fps, timeMode[TIME_CODE_CONTAINER_LOCATION.SOURCE_MEDIA]
        )
        const stepSec = getFrameStep({ frameCount, fps, isDF: dropFrame, negative: !forward })
        const time = RangeTools.fitTo([ 0, duration ], stepSec)

        pause()
        seek(time)
      }
    },
    [ fps, pause, seek, timeCodeState, previewPlayerType, activePreview, timeMode ]
  ))

  const onNextFrame = useBind(onFrameStep, true)
  const onPrevFrame = useBind(onFrameStep, false)

  return {
    onNextFrame,
    onPrevFrame,
  }
}

// ---

function MediaPreview(props) {
  const { srcVideo, fps, previewPlayerType, ...rest } = props
  const [
    onReadyPlayer,
    { usePlayerCallback, seek, getState },
  ] = usePlayer({ isSourceMediaPlayer: rest?.isMedia })
  const [ , forceUpdate ] = React.useReducer(x => x + 1, 0)
  const { imageUrl, status, error, isTextAsset, isAudioAsset } = props
  const pause = useAction(Actions.playback.changeClipPlaybackState,
    PLAYBACK_STATE.PAUSE)
  const onBufferingProgress = React.useCallback(forceUpdate, [ ])

  const [
    { duration, progress },
    { onChangeDuration, onChangeProgress },
  ] = useMediaPreviewState({ seek })

  usePlayerState(getState)

  // ---

  const { onNextFrame, onPrevFrame } = useFrameStepHandlers(fps, {
    seek,
    usePlayerCallback,
    previewPlayerType,
  })

  const { t } = useContext(TranslationContext)

  const viewSettings = useSelector(selectViewSettings)

  const uploading = status === FILE_STATUS.UPLOAD
    || status === FILE_STATUS.IMPORT
    || status === FILE_STATUS.S3_UPLOAD
    || status === FILE_STATUS.CONVERT
    || status === FILE_STATUS.MEDIAINFO

  const rendering = status === FILE_STATUS.RENDER
  const { setDraggable } = useDnDPreview({ isMedia: rest?.isMedia, asset: rest?.asset })
  const showPlaceholder = (!srcVideo && !isTextAsset && !imageUrl) || error

  return (
    <>
      <Choose>
        <When condition={uploading || rendering}>
          <If condition={error !== undefined}>
            <div className="preview-player__error">
              <div className="preview-player__error-message">
                {t(`MEDIA_ERROR_${error.type}`)}
              </div>
            </div>
          </If>
          <If condition={error === undefined}>
            <div className="preview-placeholder-base">
              {viewSettings[ViewSettings.showMenuTabs]
                ? (
                  <>
                    <div className="preview-placeholder-base__logo-importing">
                      <If condition={uploading}>
                        <IconUpload />
                      </If>

                      <If condition={rendering}>
                        <IconRendering />
                      </If>
                    </div>

                    <div className="preview-placeholder-base__text-importing">
                      <If condition={uploading}>
                        {t('MEDIA_PREVIEW_TEXT_IMPORTING')}
                        ...
                      </If>

                      <If condition={rendering}>
                        {t('MEDIA_PREVIEW_TEXT_RENDERING')}
                        ...
                      </If>
                    </div>
                  </>
                ) : (
                  <MediaProgress
                    progress={rest.asset?.progress}
                    status={status}
                    endless={(status === FILE_STATUS.IMPORT && rest.asset?.progress === 0)
                    || status === FILE_STATUS.MEDIAINFO}
                    size={100}
                    thickness={2}
                    className="media-preview-progress"
                  />
                )}
            </div>
          </If>
        </When>
        <Otherwise>
          <Preview
            {...rest}
            showPlaceholder={showPlaceholder}
            fps={fps}
            duration={duration}
            progress={progress}
            onNextFrame={onNextFrame}
            onPrevFrame={onPrevFrame}
            onRewindTimeline={seek}
            previewPlayerType={previewPlayerType}
          >
            {playerProps => (
              <>
                <If condition={imageUrl && !isTextAsset}>
                  <SourceImagePreview
                    imageUrl={imageUrl}
                    setDraggable={setDraggable}
                    styles={playerProps?.style}
                  />
                </If>
                <If condition={isTextAsset && !imageUrl}>
                  <SourceTextPreview
                    styles={playerProps?.style}
                    isTextAsset={isTextAsset}
                    textSettings={rest?.asset?.settings ?? {}}
                  />
                </If>
                {/* note that this will work for audio content too */}
                <If condition={!imageUrl && !isTextAsset && !error}>
                  <BasePreviewPlayer
                    {...playerProps}
                    isPrimary
                    id={rest?.id}
                    isAudioAsset={isAudioAsset}
                    onReady={player => onReadyPlayer(player)}
                    src={srcVideo || ''}
                    onDuration={onChangeDuration}
                    onProgress={onChangeProgress}
                    onBufferingProgress={onBufferingProgress}
                    onEnded={pause}
                  />
                </If>
              </>
            )}
          </Preview>
        </Otherwise>
      </Choose>
    </>
  )
}

MediaPreview.propTypes = {
  srcVideo: PropTypes.string,
  fps: PropTypes.number,
  imageUrl: PropTypes.string,
  error: PropTypes.shape({ type: PropTypes.string }),
  status: PropTypes.string,
  isMedia: PropTypes.bool,
  previewPlayerType: PropTypes.string,
  isTextAsset: PropTypes.bool,
  isAudioAsset: PropTypes.bool,
}

MediaPreview.defaultProps = {
  fps: DEFAULT_FPS,
  imageUrl: undefined,
  status: undefined,
  error: undefined,
  isMedia: false,
  previewPlayerType: '',
  srcVideo: '',
  isTextAsset: false,
  isAudioAsset: false,
}

export default React.memo(MediaPreview)
